import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import styled, { css, keyframes } from "styled-components";
import {
  FiArrowRight,
  FiCheck,
  FiCopy,
  FiGift,
  FiShare2,
} from "react-icons/fi";

import { colors } from "theme/colors";
import { analytics } from "MVVM/Analytics";
import { NAV_LINKS } from "MVVM/Components/Sidebar/utils";
import { useClipRewards } from "MVVM/Hooks/useClipRewards";
import { useEarnLoop } from "MVVM/Hooks/useEarnLoop";
import { useIsInternalAccount } from "MVVM/Hooks/useIsInternalAccount";
import { useUser } from "MVVM/Hooks/useUser";
import { BOOST_COPY, STEP_COPY } from "MVVM/Utilities/Rewards/boosts";
import { addAttributionParams } from "MVVM/Utilities/Attribution";
import { celebrate } from "MVVM/Utilities/Celebrate";

const SURFACE = "Clip Page";

// TODO: remove before merge. Staff are an unlimited tier, so they get a sample
// limit; their boosts and daily claim are still their real ones.
const PREVIEW_LIMIT = { limit: 10, used: 7 };

interface IEarnCardProps {
  isOwner: boolean;
  shareId: string;
}

// ALL-2078: a free earn loop player's own clip is where they are most likely
// to share, so it shows what they have left and the ways to earn more.
export const EarnCard = ({ isOwner, shareId }: IEarnCardProps) => {
  const { allstarUser } = useUser();
  const earnLoop = useEarnLoop();
  const isInternal = useIsInternalAccount();
  const eligible =
    isOwner &&
    !!allstarUser?.loggedIn &&
    (allstarUser?.user?.userGroup?.type === "FREE" || isInternal) &&
    earnLoop.enabled;
  const rewards = useClipRewards({ skip: !eligible });
  const { claimDailyClip, claiming, freeDaysLeft, openBoosts } = rewards;
  const preview = isInternal && rewards.quota?.limit === null;
  const quota = preview
    ? { ...rewards.quota, ...PREVIEW_LIMIT }
    : rewards.quota;
  const [claimed, setClaimed] = useState(false);
  const [copied, setCopied] = useState(false);
  const claimButton = useRef<HTMLButtonElement>(null);
  const viewed = useRef(false);

  const limit: number | null = quota?.limit ?? null;
  const used: number = quota?.used ?? 0;
  const left = limit === null ? 0 : Math.max(limit - used, 0);
  const nextBoost = openBoosts.find((boost) => BOOST_COPY[boost.type]);
  // Same bar as /rewards: invites stay open, but with the daily clips spent
  // and every boost done there is nothing here worth the space.
  const nothingToEarn = !nextBoost && freeDaysLeft <= 0 && !claimed;
  const visible = eligible && !!quota && limit !== null && !nothingToEarn;

  useEffect(() => {
    if (!visible || viewed.current) return;
    viewed.current = true;
    analytics.track("Clip Page - Earn Viewed", {
      tier: quota?.tier,
      used: quota?.used,
      limit: quota?.limit,
      freeDaysLeft,
      canClaimToday: Boolean(quota?.canClaimToday),
      openBoosts: openBoosts.map((boost) => boost.type),
    });
  }, [visible, quota, freeDaysLeft, openBoosts]);

  if (!visible) return null;

  const track = (lever: string) =>
    analytics.track("Clip Page - Earn Clicked", { lever });

  // The click is intent; the grant is only logged once the server confirms it.
  const claim = async () => {
    track("daily");
    const origin = claimButton.current?.getBoundingClientRect();
    let granted;
    try {
      granted = (await claimDailyClip()).data?.dailyClipClaim;
    } catch (e) {
      analytics.track("Daily Clip Claim Failed", { surface: SURFACE });
      return;
    }
    analytics.track("Daily Clip Claimed", {
      surface: SURFACE,
      streakDays: granted?.streakDays,
      freeDaysLeft: (granted?.freeDayLimit ?? 0) - (granted?.freeDays ?? 0),
    });
    setClaimed(true);
    celebrate(origin);
  };

  // The same attributed link the share buttons below copy.
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(
        addAttributionParams(
          `${window.location.origin}/clip/${shareId}`,
          allstarUser?.user?._id,
        ),
      );
    } catch (e) {
      return;
    }
    track("share");
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const usedPct = limit ? Math.min((used / limit) * 100, 100) : 0;
  const boostCopy = nextBoost && BOOST_COPY[nextBoost.type];
  const nextStep = nextBoost?.steps?.find((step) => !step.completed);
  const step = nextStep ? STEP_COPY[nextStep.step] : undefined;

  return (
    <Card aria-labelledby="clip-earn-title">
      <Balance>
        <h3 id="clip-earn-title">
          You have <strong>{left}</strong> {left === 1 ? "clip" : "clips"} left
          this month
        </h3>
        <Bar
          role="meter"
          aria-label="Clips used this month"
          aria-valuemin={0}
          aria-valuemax={limit}
          aria-valuenow={Math.min(used, limit)}
        >
          <Fill
            $pct={usedPct}
            $level={
              usedPct >= 100 ? "alert" : usedPct >= 90 ? "caution" : "normal"
            }
          />
        </Bar>
      </Balance>

      {(freeDaysLeft > 0 || claimed) && (
        <Daily aria-live="polite">
          <Token $claimed={claimed} aria-hidden="true">
            {claimed ? <FiCheck size={18} /> : <FiGift size={18} />}
          </Token>
          <div>
            <h4>
              {claimed
                ? "Claimed, your clip is ready"
                : quota.canClaimToday
                ? "Your free clip is ready"
                : "Come back tomorrow for your free clip"}
            </h4>
            <p>
              {claimed
                ? "It doesn't count toward your limit."
                : `It doesn't count toward your limit. ${freeDaysLeft} left this month.`}
            </p>
          </div>
          {!claimed && quota.canClaimToday && (
            <Claim
              ref={claimButton}
              type="button"
              onClick={claim}
              disabled={claiming}
            >
              Claim
            </Claim>
          )}
        </Daily>
      )}

      {nextBoost && boostCopy && (
        <Boost>
          <Tile $brand={boostCopy.brand} aria-hidden="true">
            <boostCopy.Icon size={18} />
          </Tile>
          <div>
            <h4>
              <span>{boostCopy.short}</span>
              <Reward>
                +{nextBoost.clips} <small>clips</small>
              </Reward>
            </h4>
            <p>{step ? `Next: ${step.label}` : boostCopy.hook}</p>
          </div>
          <SetUp
            to={step?.to ?? NAV_LINKS.rewards}
            onClick={() => track(nextBoost.type)}
          >
            Set up <FiArrowRight aria-hidden="true" />
          </SetUp>
        </Boost>
      )}

      <Boost>
        <Tile $brand={colors.chalkAlpha10} aria-hidden="true">
          <FiShare2 size={18} />
        </Tile>
        <div>
          <h4>
            <span>Share this clip</span>
            <Reward>
              +5 <small>clips</small>
            </Reward>
          </h4>
          <p>
            When a friend joins from your link and connects a game, you both get
            5 clips.
          </p>
        </div>
        <CopyLink type="button" onClick={copyLink}>
          {copied ? (
            <FiCheck aria-hidden="true" />
          ) : (
            <FiCopy aria-hidden="true" />
          )}
          {copied ? "Copied" : "Copy link"}
        </CopyLink>
      </Boost>

      <AllWays to={NAV_LINKS.rewards} onClick={() => track("all rewards")}>
        All ways to earn
      </AllWays>
    </Card>
  );
};

// The token lands once, when the claim goes through.
const land = keyframes`
  0% { transform: scale(0.9); }
  60% { transform: scale(1.04); }
  100% { transform: scale(1); }
`;

// Flat on the rail, like the clip info above it; only the rows are boxed.
const Card = styled.section`
  display: grid;
  gap: 12px;
  margin: 0 25px 18px;
  color: ${({ theme }) => theme.colors.ice};

  h4 {
    margin: 0;
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    column-gap: 6px;
    font-family: Barlow, sans-serif;
    font-size: 15px;
    font-weight: 700;
    line-height: 20px;
  }

  p {
    margin: 2px 0 0;
    color: ${({ theme }) => theme.colors.chalk};
    font-size: 13px;
    line-height: 18px;
    text-wrap: pretty;
  }

  @media (max-width: ${({ theme }) => theme.breaks.narrow}) {
    margin: 0 0 16px;
  }
`;

const Balance = styled.div`
  display: grid;
  gap: 8px;
  margin-bottom: 2px;

  h3 {
    margin: 0;
    font-family: Barlow, sans-serif;
    font-size: 16px;
    font-weight: 600;
    line-height: 22px;
  }

  strong {
    font-style: italic;
    font-weight: 900;
    font-size: 20px;
    letter-spacing: -0.02em;
    font-variant-numeric: tabular-nums;
  }
`;

const Bar = styled.div`
  height: 6px;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.chalkAlpha10};
  overflow: hidden;
`;

const Fill = styled.div<{
  $pct: number;
  $level: "normal" | "caution" | "alert";
}>`
  width: ${({ $pct }) => $pct}%;
  height: 100%;
  border-radius: 999px;
  background: ${({ theme, $level }) =>
    $level === "alert"
      ? theme.colors.alert
      : $level === "caution"
      ? theme.colors.caution
      : theme.colors.envy};
`;

const row = css`
  display: grid;
  grid-template-columns: 36px minmax(0, 1fr) auto;
  align-items: center;
  gap: 12px;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
`;

const Daily = styled.div`
  ${row}

  h4 {
    text-wrap: balance;
  }
  background: linear-gradient(
      ${({ theme }) => theme.colors.envyAlpha05} 0%,
      transparent 100%
    ),
    ${({ theme }) => theme.colors.navy300};
`;

const Boost = styled.div`
  ${row}
  background: ${({ theme }) => theme.colors.navy300};
`;

const tile = css`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: grid;
  place-items: center;
`;

const Token = styled.span<{ $claimed: boolean }>`
  ${tile}
  background: ${({ theme }) => theme.colors.envyAlpha20};
  color: ${({ theme }) => theme.colors.envy};
  animation: ${({ $claimed }) => ($claimed ? land : "none")} 0.42s
    cubic-bezier(0.16, 0.84, 0.34, 1) both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Tile = styled.span<{ $brand: string }>`
  ${tile}
  background: ${({ $brand }) => $brand};
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha40};
`;

const Reward = styled.span`
  font-family: Barlow, sans-serif;
  font-style: italic;
  font-weight: 900;
  font-size: 16px;
  letter-spacing: -0.02em;
  font-variant-numeric: tabular-nums;
  color: ${({ theme }) => theme.colors.envy};
  white-space: nowrap;

  small {
    margin-left: 2px;
    font-style: normal;
    font-weight: 600;
    font-size: 11px;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: ${({ theme }) => theme.colors.chalk};
  }
`;

const control = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 36px;
  padding: 0 16px;
  border-radius: 999px;
  font-family: Barlow, sans-serif;
  font-size: 14px;
  font-weight: 700;
  white-space: nowrap;
  text-decoration: none;
  cursor: pointer;
  transition:
    background-color 0.15s ease-out,
    border-color 0.15s ease-out;

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.envy};
    outline-offset: 2px;
  }
`;

const Claim = styled.button`
  ${control}
  background: ${({ theme }) => theme.colors.envyAlpha20};
  border: 1px solid ${({ theme }) => theme.colors.envy};
  color: ${({ theme }) => theme.colors.ice};

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.envyAlpha40};
  }

  &:disabled {
    cursor: progress;
    opacity: 0.6;
  }
`;

const secondary = css`
  ${control}
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.chalkAlpha20};
  color: ${({ theme }) => theme.colors.ice};

  &:hover {
    border-color: ${({ theme }) => theme.colors.chalkAlpha40};
    background: ${({ theme }) => theme.colors.chalkAlpha10};
    color: ${({ theme }) => theme.colors.ice};
  }
`;

const SetUp = styled(Link)`
  ${secondary}
`;

const CopyLink = styled.button`
  ${secondary}
`;

const AllWays = styled(Link)`
  justify-self: start;
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 13px;
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 3px;
  text-decoration-color: ${({ theme }) => theme.colors.chalkAlpha40};

  &:hover {
    color: ${({ theme }) => theme.colors.ice};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.envy};
    outline-offset: 2px;
  }
`;
